'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'

import { Icon } from '@circlefin/components/lib/Icon'
import { SelectCard } from '@circlefin/components/lib/SelectCard'
import { SkeletonBox } from '@circlefin/components/lib/SkeletonBox'
import { useForm } from '@circlefin/form/useForm'
import {
  CWHeading,
  CWHeadingHeader,
  CWHeadingIntro,
} from '@features/common.components/ComponentsWeb/Heading'
import { RecaptchaDisclaimer } from '@features/common.components/RecaptchaDisclaimer'
import { GoogleV2ReCaptchaVerifier } from '@features/common.containers/GoogleV2ReCaptchaVerifier'
import { MaintenanceMode } from '@features/faucet.components/MaintenanceMode'
import { RateLimit, getFaucetAmount } from '@features/faucet.constants'
import { useFeatureFlag } from '@features/flags.hooks/useFeatureFlag'
import useTranslation from 'next-translate/useTranslation'
import * as y from 'yup'

import type { IconName } from '@circlefin/components/lib/Icon'
import type { SubmitHandler } from '@circlefin/form/useForm/useForm'
import type { UseAugmentedReCaptchaResult } from '@features/common.hooks/useAugmentedReCaptcha'
import type {
  Currency,
  Blockchain,
  RequestTokenInput,
  FaucetConfiguration,
} from '@features/faucet.graphql'
import type { FaucetEventOverrideAmounts } from '@features/faucet.graphql/datasources/FaucetApi/eventOverrides'

const CURRENCY_DISPLAY_NAMES: Partial<Record<string, string>> = {
  CIRBTC: 'cirBTC',
}

const getDisplayName = (currency: string): string =>
  CURRENCY_DISPLAY_NAMES[currency] ?? currency

const schema = y.object({
  network: y.string<Blockchain>().required(),
  address: y
    .string()
    .required('common:forms.validation.required')
    .when(['network'], ([network]: Blockchain[], schema) =>
      schema.blockchains({
        abbreviation: network as never,
      }),
    ),
})

type FormSchema = y.InferType<typeof schema>

export interface RequestTokenFormProps {
  /**
   * Configuration for the faucet.
   */
  data?: FaucetConfiguration
  /**
   * Optional event-scoped amount overrides to reflect in the V1 UI.
   */
  eventOverrideAmounts?: FaucetEventOverrideAmounts
  /**
   * Indicates whether the form is currently loading.
   */
  loading: boolean
  /**
   * Function to be called when the form is submitted.
   * @param input - The request token input data.
   * @returns A Promise that resolves when the form submission is complete.
   */
  onSubmit: (input: RequestTokenInput) => Promise<void>
  /**
   * ReCAPTCHA V2 state.
   */
  recaptchaV2: UseAugmentedReCaptchaResult[1]['v2']
}

export const RequestTokenForm: React.FC<RequestTokenFormProps> = ({
  loading,
  onSubmit,
  data,
  eventOverrideAmounts,
  recaptchaV2,
}) => {
  const { t } = useTranslation('faucet')

  const faucetFaqEnabled = useFeatureFlag('faucetFaqEnabled')

  const [selectedCurrency, setSelectedCurrency] = useState<Currency>(
    () => 'USDC',
  )
  const [Form, { trigger, watch, resetField }] = useForm<FormSchema>({
    schema,
    defaultValues: { network: 'ARC' },
  })

  const address = watch('address')
  const selectedNetwork = watch('network')
  const submitAmount =
    eventOverrideAmounts?.[selectedNetwork]?.[selectedCurrency] ??
    getFaucetAmount(selectedCurrency)

  const handleCurrencySelection = useCallback(
    (value: Currency) => () => {
      setSelectedCurrency(value)
      resetField('network')
    },
    [resetField],
  )

  useEffect(() => {
    if (address) void trigger('address')
  }, [address, selectedNetwork, trigger])

  const handleSubmit: SubmitHandler<FormSchema> = useCallback(
    async (input) => {
      try {
        await onSubmit({
          destinationAddress: input.address,
          token: selectedCurrency,
          blockchain: input.network,
        })
      } catch (error) {
        console.error('[Faucet Form] Form submission failed:', error)
        throw error
      }
    },
    [onSubmit, selectedCurrency],
  )

  const globalMaintenanceModeEnabled = data?.maintenanceMode === true

  const chainMaintenanceModeEnabled = useMemo(() => {
    return (
      data?.chains.find((chain) => chain.name === selectedNetwork)
        ?.maintenanceMode ?? false
    )
  }, [data, selectedNetwork])

  const hasNotCompletedRecaptcha = useMemo(() => {
    return recaptchaV2.enabled && recaptchaV2.token == null
  }, [recaptchaV2])

  return (
    <div className="w-full" data-testid="request-token-form">
      <MaintenanceMode enabled={globalMaintenanceModeEnabled} />

      {faucetFaqEnabled && (
        <CWHeading className="mb-6" size="xl">
          <CWHeadingHeader className="type-header-xl">
            {t`requestTokenForm.header`}
          </CWHeadingHeader>
          <CWHeadingIntro className="type-body-lg">
            {t`requestTokenForm.intro`}
          </CWHeadingIntro>
        </CWHeading>
      )}

      <Form className="w-full space-y-4" onSubmit={handleSubmit}>
        <div className="flex w-full space-x-4">
          <SkeletonBox className="h-[124px] w-full" loading={loading}>
            {data?.currencies.map((currency) => (
              <SelectCard
                key={currency}
                checked={selectedCurrency === currency}
                className="w-full"
                data-testid={`select-card-${currency}`}
                disabled={globalMaintenanceModeEnabled}
                id="currency"
                label=""
                name="currency"
                onChange={handleCurrencySelection(currency)}
                type="radio"
                value={currency}
                variant="primary/base"
                hideCheck
              >
                <div className="flex w-full flex-col items-center justify-center space-y-4">
                  <Icon name={currency as IconName} size={48} />
                  <div className="type-body-base-bold">
                    {getDisplayName(currency)}
                  </div>
                </div>
              </SelectCard>
            ))}
          </SkeletonBox>
        </div>
        <SkeletonBox className="h-16 w-full" loading={loading}>
          <Form.Dropdown
            data-testid="network-dropdown"
            disabled={globalMaintenanceModeEnabled}
            items={
              data?.chains
                .filter((chain) => chain.currencies.includes(selectedCurrency))
                .map((chain) => ({
                  label: t(`chains.${chain.name}`),
                  value: chain.name,
                  // Components-web does not support "ARC" as an icon name,
                  // we can leverage the blockchain icon for this case.
                  // We should use blockchain icons for all chains.
                  icon: (chain.name === 'ARC'
                    ? 'Blockchain/Arc'
                    : chain.name) as never,
                })) || []
            }
            label={t`requestTokenForm.labels.network`}
            name="network"
          />
        </SkeletonBox>
        <MaintenanceMode enabled={chainMaintenanceModeEnabled} />
        <SkeletonBox className="h-[84px] w-full" loading={loading}>
          <Form.Input
            disabled={
              globalMaintenanceModeEnabled || chainMaintenanceModeEnabled
            }
            label={t`requestTokenForm.labels.destinationAddress`}
            message={t('requestTokenForm.limit', {
              count: RateLimit.FAUCET_RATE_LIMIT_HOURS,
            })}
            name="address"
            placeholder={t`requestTokenForm.labels.destinationAddressPlaceholder`}
          />
        </SkeletonBox>
        <SkeletonBox className="h-10 w-full" loading={loading}>
          {recaptchaV2.enabled ? (
            <GoogleV2ReCaptchaVerifier onVerified={recaptchaV2.handleVerify} />
          ) : (
            <RecaptchaDisclaimer />
          )}
          <Form.SubmitButton
            className="w-full"
            disabled={
              globalMaintenanceModeEnabled ||
              chainMaintenanceModeEnabled ||
              hasNotCompletedRecaptcha
            }
            label=""
            variant="primary"
          >
            {t('requestTokenForm.submit', {
              amount: submitAmount,
              currency: getDisplayName(selectedCurrency),
            })}
          </Form.SubmitButton>
        </SkeletonBox>
      </Form>
    </div>
  )
}
